import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, PerspectiveCamera, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import BalloonsAnimation from '../components/BalloonsAnimation';
import { useMusic } from '../context/MusicContext';

const SurpriseContainer = styled.div`
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  margin-bottom: 20px;
  position: relative;
`;

const PageTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 40px;
  font-size: 2.5rem;
`;

const Description = styled.p`
  text-align: center;
  max-width: 600px;
  margin-bottom: 40px;
  line-height: 1.6;
  color: var(--dark-text);
`;

const CanvasContainer = styled.div`
  width: 100%;
  height: 500px;
  background-color: #f8f8f8;
  background-image: url('/img/image_surprise/compressed/mont-blanc.jpg');
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const Instruction = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px 20px;
  border-radius: 30px;
  font-weight: 600;
  color: var(--primary-color);
  z-index: 10;
  pointer-events: none;
`;

const VideoSection = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 40px auto 0;
  display: ${props => props.show ? 'block' : 'none'};
  animation: ${props => props.show ? 'fadeIn 1s ease' : 'none'};
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  aspect-ratio: 16 / 9;
`;

const VideoTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.2rem;
  color: var(--primary-color);
`;

// Création d'un gâteau d'anniversaire
const BirthdayCake = ({ position = [0, 0, 0], visible = false }) => {
  // Référence pour l'animation
  const cakeRef = useRef();
  const flameRef = useRef();
  
  // Animation de rotation et de la flamme
  useEffect(() => {
    if (visible) {
      const interval = setInterval(() => {
        if (cakeRef.current) {
          cakeRef.current.rotation.y += 0.01;
        }
        if (flameRef.current) {
          // Animation de la flamme qui vacille
          flameRef.current.intensity = 1 + Math.sin(Date.now() * 0.01) * 0.3;
        }
      }, 16);
      return () => clearInterval(interval);
    }
  }, [visible]);
  
  if (!visible) return null;
  
  return (
    <group 
      position={position} 
      ref={cakeRef} 
      scale={[0.7, 0.7, 0.7]}
    >
      {/* Base du gâteau */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[2, 2, 1, 32]} />
        <meshStandardMaterial color="#F2D2BD" />
      </mesh>
      
      {/* Couche intermédiaire */}
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[1.7, 1.7, 0.5, 32]} />
        <meshStandardMaterial color="#FFE4B5" />
      </mesh>
      
      {/* Couche supérieure */}
      <mesh position={[0, 1.25, 0]}>
        <cylinderGeometry args={[1.4, 1.4, 0.5, 32]} />
        <meshStandardMaterial color="#F2D2BD" />
      </mesh>
      
      {/* Glaçage supérieur */}
      <mesh position={[0, 1.55, 0]}>
        <cylinderGeometry args={[1.45, 1.45, 0.1, 32]} />
        <meshStandardMaterial color="#FF9999" />
      </mesh>
      
      {/* Texte "Bonne Fête Papa" */}
      <mesh position={[0, 1.61, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[2, 2]} />
        <meshBasicMaterial transparent opacity={0.9}>
          <canvasTexture attach="map" args={[createTextTexture("Bonne Fête Papa")]} />
        </meshBasicMaterial>
      </mesh>
      
      {/* Bougies */}
      {Array.from({ length: 5 }).map((_, i) => {
        const angle = (i / 5) * Math.PI * 2;
        const radius = 0.8;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        
        return (
          <group key={i} position={[x, 1.6, z]}>
            {/* Bougie */}
            <mesh>
              <cylinderGeometry args={[0.05, 0.05, 0.5, 16]} />
              <meshStandardMaterial color={["#FF6B6B", "#4A90E2", "#FFD700", "#66BB6A", "#9C27B0"][i % 5]} />
            </mesh>
            
            {/* Flamme */}
            <mesh position={[0, 0.35, 0]}>
              <sphereGeometry args={[0.08, 16, 16]} />
              <meshStandardMaterial color="#FFD700" emissive="#FF4500" emissiveIntensity={2} />
              <pointLight ref={i === 0 ? flameRef : null} color="#FF6600" intensity={1} distance={3} />
            </mesh>
          </group>
        );
      })}
      
      {/* Décorations */}
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        const radius = 1.8;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        
        return (
          <mesh key={i} position={[x, 0.5, z]} scale={[0.2, 0.2, 0.2]}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color={["#FF6B6B", "#4A90E2", "#FFD700", "#66BB6A"][i % 4]} />
          </mesh>
        );
      })}
    </group>
  );
};

// Fonction pour créer une texture avec du texte
function createTextTexture(text) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext('2d');
  
  // Fond transparent
  context.fillStyle = 'rgba(255, 255, 255, 0)';
  context.fillRect(0, 0, canvas.width, canvas.height);
  
  // Texte
  context.font = 'bold 36px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillStyle = '#333333';
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  
  return canvas;
}

// Création de la boîte cadeau
const GiftBox = ({ position = [0, 0, 0], isOpen, toggleOpen }) => {
  // Références pour l'animation
  const bottomRef = useRef();
  const topRef = useRef();
  const ribbonRef = useRef();
  
  // Animation d'ouverture
  useEffect(() => {
    if (isOpen && topRef.current) {
      topRef.current.position.y = 4;
    } else if (topRef.current) {
      topRef.current.position.y = 0.5;
    }
  }, [isOpen]);
  
  return (
    <group position={position} onClick={toggleOpen}>
      {/* Boîte inférieure */}
      <mesh ref={bottomRef} position={[0, -0.5, 0]}>
        <boxGeometry args={[3, 1, 3]} />
        <meshStandardMaterial color="#4A90E2" />
      </mesh>
      
      {/* Boîte supérieure */}
      <mesh 
        ref={topRef} 
        position={[0, isOpen ? 4 : 0.5, 0]}
        rotation={[isOpen ? Math.PI / 6 : 0, 0, 0]}
      >
        <boxGeometry args={[3.2, 0.5, 3.2]} />
        <meshStandardMaterial color="#4A90E2" />
      </mesh>
      
      {/* Ruban */}
      <group ref={ribbonRef}>
        {/* Ruban horizontal */}
        <mesh position={[0, 0, 0]} rotation={[0, 0, 0]}>
          <boxGeometry args={[3.3, 0.3, 0.3]} />
          <meshStandardMaterial color="#D4AF37" />
        </mesh>
        
        {/* Ruban vertical */}
        <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <boxGeometry args={[3.3, 0.3, 0.3]} />
          <meshStandardMaterial color="#D4AF37" />
        </mesh>
        
        {/* Nœud */}
        <mesh position={[0, 0.3, 0]}>
          <sphereGeometry args={[0.4, 16, 16]} />
          <meshStandardMaterial color="#D4AF37" />
        </mesh>
      </group>
    </group>
  );
};

// Scène 3D
const Scene = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleOpen = () => {
    if (!isOpen) {
      // Déclencher l'événement personnalisé pour les ballons
      const event = new CustomEvent('triggerBalloons');
      window.dispatchEvent(event);
    }
    setIsOpen(!isOpen);
  };
  
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <PerspectiveCamera makeDefault position={[0, 2, 8]} />
      <OrbitControls enableZoom={false} enablePan={false} minPolarAngle={Math.PI / 4} maxPolarAngle={Math.PI / 2} />
      
      <GiftBox position={[0, 0, 0]} isOpen={isOpen} toggleOpen={toggleOpen} />
      <BirthdayCake position={[0, 0, 0]} visible={isOpen} />
    </>
  );
};

const Surprise = () => {
  const [showBalloons, setShowBalloons] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [cakeVisible, setCakeVisible] = useState(false);
  const iframeRef = useRef(null);
  const { setVideoRef, setIsPlaying } = useMusic();
  
  useEffect(() => {
    // Gestionnaire d'événement pour les ballons
    const handleBalloons = () => {
      setShowBalloons(true);
      // Rendre le gâteau visible après l'ouverture du cadeau
      setCakeVisible(true);
    };
    
    window.addEventListener('triggerBalloons', handleBalloons);
    
    return () => {
      window.removeEventListener('triggerBalloons', handleBalloons);
    };
  }, []);
  
  // Fonction appelée lorsque l'animation de ballons est terminée
  const handleBalloonsComplete = () => {
    setShowBalloons(false);
  };
  
  // Fonction pour afficher la vidéo
  const handleShowVideo = () => {
    setShowVideo(true);
  };
  
  // Référencer l'iframe dans le contexte musical une fois qu'elle est visible
  useEffect(() => {
    if (showVideo && iframeRef.current) {
      // Ajouter l'API YouTube à l'iframe
      const currentSrc = iframeRef.current.src;
      if (!currentSrc.includes('enablejsapi=1')) {
        iframeRef.current.src = currentSrc + (currentSrc.includes('?') ? '&' : '?') + 'enablejsapi=1';
      }
      
      // Attendre que l'iframe soit complètement chargée
      const handleIframeLoad = () => {
        setVideoRef(iframeRef.current);
        setIsPlaying(true);
      };
      
      iframeRef.current.addEventListener('load', handleIframeLoad);
      
      return () => {
        if (iframeRef.current) {
          iframeRef.current.removeEventListener('load', handleIframeLoad);
        }
        setVideoRef(null);
        setIsPlaying(false);
      };
    }
  }, [showVideo, setVideoRef, setIsPlaying]);
  
  return (
    <SurpriseContainer>
      <PageTitle>Une surprise pour Papa</PageTitle>
      <Description>
        Cliquez sur le cadeau pour découvrir une surprise spéciale pour la Fête des Pères !
        {cakeVisible && <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#4A90E2' }}>
          Le gâteau est apparu ! Cliquez sur le bouton ci-dessous pour voir une autre surprise !
        </div>}
      </Description>
      
      <BalloonsAnimation 
        show={showBalloons} 
        onComplete={handleBalloonsComplete} 
        message="Bonne Fête Papa !"
      />
      
      <CanvasContainer>
        <Canvas shadows>
          <Scene />
        </Canvas>
        <Instruction>
          {cakeVisible ? "Regardez le gâteau tourner !" : "Cliquez sur le cadeau"}
        </Instruction>
      </CanvasContainer>
      
      {/* Bouton pour afficher la vidéo quand le gâteau est visible */}
      {cakeVisible && !showVideo && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <button 
            onClick={handleShowVideo}
            style={{
              padding: '15px 30px',
              backgroundColor: '#4A90E2',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease'
            }}
          >
            Voir la vidéo surprise Thunderstruck
          </button>
        </div>
      )}
      
      {showVideo && (
        <VideoSection show={true} id="video-section">
          <VideoTitle>Thunderstruck pour toi, Papa !</VideoTitle>
          <VideoContainer>
            <iframe 
              ref={iframeRef}
              width="100%" 
              height="100%" 
              src="https://www.youtube-nocookie.com/embed/v2AC41dglnM?si=c-0P9UUCDNcVAuob&autoplay=1&start=8&enablejsapi=1" 
              title="YouTube video player" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            ></iframe>
          </VideoContainer>
        </VideoSection>
      )}
    </SurpriseContainer>
  );
};

export default Surprise;
